<template>
  <!-- eslint-disable @intlify/vue-i18n/no-raw-text -->
  <div
    v-if="showClippy"
    class="clippy__wrapper"
  >
    <div class="clippy__balloon">
      <div class="clippy__content">
        <template v-if="!component">
          It looks like you're trying to debug something.
          <br><br>
          Would you like help?
          <ul>
            <template
              v-for="(action, index) in actions"
              :key="index"
            >
              <li
                v-if="isActionWithComponent(action)"
                class="clickable"
                @click="onSetComponent(action.component)"
              >
                {{ action.message }}
              </li>
              <li v-else-if="isActionWithOptions(action)">
                {{ action.message }}
                <ul>
                  <li
                    v-for="(opt, index2) in action.options"
                    :key="index2"
                    class="clickable"
                    @click="action.handler(opt)"
                  >
                    {{ opt }}
                  </li>
                </ul>
              </li>
            </template>
          </ul>
        </template>

        <component
          :is="component"
          v-else
        />
      </div>

      <q-icon
        class="clippy__close"
        name="close"
        size="16px"
        @click="onClose"
      />
    </div>

    <a
      class="clippy__icon"
      href="javascript:window.clippy()"
    >
      <span class="print-only">RVAI Clippy</span>
      <span :class="decoration" />
    </a>
  </div>
</template>

<script lang="ts" setup>
import saveAs from 'file-saver'
import { storeToRefs } from 'pinia'
import type { Component } from 'vue'
import {
  computed,
  onMounted,
  onUnmounted,
  ref,
} from 'vue'
import { i18nInstance } from '~i18n'
import { useSessionStore } from '~stores/useSessionStore'
import { DEFAULT_LOCALE } from '~utils/localization'
import {
  clippyActions,
  isActionWithComponent,
  isActionWithOptions,
} from './clippyActions'


const KONAMI_CODE = [
  'ArrowUp',
  'ArrowUp',
  'ArrowDown',
  'ArrowDown',
  'ArrowLeft',
  'ArrowRight',
  'ArrowLeft',
  'ArrowRight',
  'KeyB',
  'KeyA',
]

const { currentUser } = storeToRefs(useSessionStore())

const component = ref<Component | undefined>(undefined)
const position = ref(0)
const visible = ref(false)

const now = new Date()

const actions = computed(() => {
  return clippyActions.filter(x => x.available == null || x.available())
})

const isEaster = computed(() => {
  const year = now.getFullYear()
  const f = Math.floor
  const g = year % 19
  const c = f(year / 100)
  const h = (c - f(c / 4) - f((8 * c + 13) / 25) + 19 * g + 15) % 30
  const i = h - f(h / 28) * (1 - f(29 / (h + 1)) * f((21 - g) / 11))
  const j = (year + f(year / 4) + i + 2 - c + f(c / 4)) % 7
  const l = i - j
  const month = 3 + f((l + 40) / 44)
  const day = l + 28 - 31 * f(month / 4)
  const date = new Date(year, month - 1, day)
  const delta = date.getTime() - now.getTime()
  return delta >= 0 && delta <= 604800000
})

const isXmas = computed(() => {
  return now.getMonth() === 11 && now.getDate() <= 25
})

const isSpoOktober = computed(() => {
  return now.getMonth() === 9
})

const decoration = computed(() => {
  if (isXmas.value) return 'xmas'
  if (isEaster.value) return 'easter'
  if (isSpoOktober.value) return 'halloween'
  return 'hidden'
})

const showClippy = computed(() => {
  return !!currentUser.value && visible.value
})

function konamiCheck (event: KeyboardEvent) {
  if (event.code === KONAMI_CODE[position.value++]) {
    if (position.value === KONAMI_CODE.length) {
      onShow()
      position.value = 0
    }
  } else {
    position.value = 0
  }
}

function onClose () {
  if (component.value) {
    component.value = undefined
  } else {
    visible.value = false
  }
}

function onSetComponent (value: Component) {
  component.value = value
}

function onShow () {
  visible.value = true
}

function getStringResource (name: string = DEFAULT_LOCALE): void {
  const blob = new Blob(
    [JSON.stringify(i18nInstance.global.getLocaleMessage(DEFAULT_LOCALE), null, 2)],
    {
      type: 'application/json',
    },
  )
  saveAs(blob, `${name}.json`)
}

onMounted(() => {
  document.addEventListener('keydown', konamiCheck, false)
  window.clippy = onShow
  window.getStringResourceFile = getStringResource
})

onUnmounted(() => {
  document.removeEventListener('keydown', konamiCheck, false)
  delete window.clippy
  delete window.getStringResourceFile
})
</script>

<style lang="sass" scoped>
.clippy
  &__wrapper
    bottom: 16px
    position: fixed
    right: 16px
    text-align: right
    z-index: 10000

  &__balloon
    background: #FFC
    border-radius: 5px
    border: 1px solid black
    color: black
    margin-bottom: 24px
    padding: 8px
    position: relative

    &::before
      border-color: transparent black transparent transparent
      border-style: solid
      border-width: 0 10px 16px 0
      content: ''
      height: 0
      position: absolute
      right: 50px
      top: 100%
      width: 0

    &::after
      border-color: transparent #FFC transparent transparent
      border-style: solid
      border-width: 0 8px 14px 0
      content: ''
      height: 0
      position: absolute
      right: 51px
      top: 100%
      width: 0

  &__content
    font-family: sans-serif
    font-size: 10pt
    text-align: left
    width: 300px

    ::v-deep(ul)
      padding-left: 20px

      & > li.clickable
        cursor: pointer

        &:hover
          text-decoration: underline

  &__close
    cursor: pointer
    position: absolute
    right: 8px
    top: 8px

  &__icon
    display: inline-block
    height: 100px
    position: relative
    width: 100px

    &:before
      background-image: url('./assets/roboclippy.gif')
      background-repeat: no-repeat
      background-size: cover
      bottom: 0
      content: ''
      left: 0
      position: absolute
      right: 0
      top: 0

    &:after
      position: absolute
      width: 80px
      height: 20px
      right: 10px
      border-radius: 50%
      z-index: -1
      bottom: -3px
      content: ''
      background: var(--q-black-1)
      opacity: 8%
      filter: blur(6px)
      transform: perspective(40px) rotateX(45deg)

    .xmas
      display: inline-block
      width: 40px
      height: 40px
      background: url('./assets/hat.png') no-repeat
      background-size: contain
      position: absolute
      bottom: 73px
      right: 27px

    .easter
      display: inline-block
      width: 36px
      height: 36px
      background: url('./assets/ears.png') no-repeat
      background-size: contain
      position: absolute
      bottom: 89px
      right: 33px

    .halloween
      display: inline-block
      width: 36px
      height: 36px
      background: url('./assets/halloween.png') no-repeat
      background-size: contain
      position: absolute
      bottom: 85px
      transform: rotate(15deg)
      right: 28px
</style>
